// Session health diagnostics.
//
// Some users get signed out seconds after a successful login. Server-side auth
// logs show the browser asking for a new token 30+ times within a few seconds
// (normal is roughly once an hour): every refresh revokes the previous one and
// the session finally dies. The usual cause is a device clock that is far off
// the real time, so every freshly issued token already looks expired locally.
//
// This module measures the device/server clock difference and counts refresh
// events so the app can stop the loop and explain what is happening instead of
// silently dropping the user on the login page.

export type SessionIssue =
  | { kind: "clock_skew"; skewSeconds: number }
  | { kind: "refresh_loop"; skewSeconds: number | null };

const RETURN_PATH_KEY = "sida.auth.returnPath";

/** Difference between device clock and server clock, in seconds (positive = device ahead). */
export async function measureClockSkewSeconds(baseUrl: string): Promise<number | null> {
  try {
    const before = Date.now();
    const res = await fetch(`${baseUrl}/auth/v1/health`, { method: "GET", cache: "no-store" });
    const header = res.headers.get("date");
    if (!header) return null;
    const serverMs = new Date(header).getTime();
    if (!Number.isFinite(serverMs)) return null;
    // Compare against the midpoint of the request to absorb network latency.
    const localMs = (before + Date.now()) / 2;
    return Math.round((localMs - serverMs) / 1000);
  } catch {
    return null;
  }
}

/** A clock this far off makes freshly issued tokens look already expired. */
export const CLOCK_SKEW_LIMIT_SECONDS = 180;

/** Counts refresh events inside a sliding window to detect a refresh loop. */
export function createRefreshLoopDetector(opts?: { windowMs?: number; limit?: number }) {
  const windowMs = opts?.windowMs ?? 15_000;
  const limit = opts?.limit ?? 8;
  let stamps: number[] = [];
  return {
    /** Returns true when the refresh rate is abnormal (a loop, not normal renewal). */
    record(now = Date.now()): boolean {
      stamps = stamps.filter((t) => now - t < windowMs);
      stamps.push(now);
      return stamps.length >= limit;
    },
    reset() {
      stamps = [];
    },
  };
}

export function rememberReturnPath(path: string) {
  try {
    if (path && path !== "/auth") sessionStorage.setItem(RETURN_PATH_KEY, path);
  } catch { /* storage unavailable */ }
}

export function takeReturnPath(): string | null {
  try {
    const value = sessionStorage.getItem(RETURN_PATH_KEY);
    if (value) sessionStorage.removeItem(RETURN_PATH_KEY);
    return value && value.startsWith("/") && !value.startsWith("//") ? value : null;
  } catch {
    return null;
  }
}
